import { useState } from "react";
import SaltoConfigsPageView from "./SaltoConfigsPageView";
import useEvaApi, { EvaApiCall } from "../../../hooks/useEvaApi";
import useCustomEffect from "../../../hooks/useCustomEffect";
import { useAuthContext } from "../../../context/AuthContext";

function SaltoConfigsPage() {
  const callEvaApi = useEvaApi();
  const authContext = useAuthContext();
  const [visitorTypes, setVisitorTypes] = useState([]);
  const [saltoResources, setSaltoResources] = useState({
    sites: [],
    locks: [],
    access_groups: [],
    iqs: [],
  });
  const [configData, setConfigData] = useState({
    id: null,
    active: false,
    client_id: "",
    client_secret: "",
    username: "",
    password: "",
    api_url: "",
    auth_url: "",
    sync_employees: false,
    sync_visitors: false,
    iqs: [],
    locks: [],
    visitor_types: [],
  });
  const [submitError, setSubmitError] = useState(null);

  /**
   * initialize the component
   */
  useCustomEffect(() => {
    fetchSaltoResources();
    fetchVisitorTypes();
    fetchConfigData();
  });

  /**
   * This method will fetch the Salto resources from the API
   */
  async function fetchSaltoResources() {
    await callEvaApi(
      new EvaApiCall("integrations/salto/resources")
        .setLoadingGroup("salto_configs_page_fetch")
        .setAlertError(false)
        .setOnSuccess((response) => {
          setSaltoResources(response.data.data);
        }),
    );
  }

  /**
   * This method will fetch the visitor types from the API
   */
  async function fetchVisitorTypes() {
    await callEvaApi(
      new EvaApiCall("visitor_types")
        .setLoadingGroup("salto_configs_page_fetch")
        .setAlertError(false)
        .setOnSuccess((response) => {
          setVisitorTypes(response.data.data.records);
        }),
    );
  }

  /**
   * This method will fetch the Salto config data from the API
   */
  async function fetchConfigData() {
    await callEvaApi(
      new EvaApiCall("integrations/salto/configs")
        .setLoadingGroup("salto_configs_page_fetch")
        .setParams(getConfigFetchParams())
        .setAlertError(true)
        .setOnSuccess((response) => {
          const data = response.data.data;
          if (!data) return;

          setConfigData(getConfigDataFromApiResponse(data));
        }),
    );
  }

  /**
   * This method will return the fetch params for the config fetch call
   * @returns {object}
   */
  function getConfigFetchParams() {
    return {
      with_salto_config_locks_data: true,
      with_salto_config_iqs_data: true,
      with_salto_config_visitor_types_data: true,
    };
  }

  /**
   * This method will map the API response to the config data structure
   * @param {object} data
   * @returns {object}
   */
  function getConfigDataFromApiResponse(data) {
    return {
      id: data.id,
      active: data.active,
      client_id: data.client_id,
      client_secret: data.client_secret,
      username: data.username,
      password: data.password,
      api_url: data.api_url,
      auth_url: data.auth_url,
      sync_employees: data.sync_employees,
      sync_visitors: data.sync_visitors,
      iqs: data.iqs.map((iq) => ({
        id: iq.id,
        iq_id: iq.iq_id,
        connected: iq.connected,
      })),
      locks: data.locks.map((lock) => ({
        id: lock.id,
        lock_id: lock.lock_id,
        location_id: lock.location,
        entry_type: lock.entry_type,
      })),
      visitor_types: data.visitor_types.map((visitorType) => ({
        visitor_type_id: visitorType.visitor_type,
        access_group_id: visitorType.access_group_id,
        role_id: visitorType.role_id,
      })),
    };
  }

  /**
   * This method will update the active status on toggle change
   * @param {bool} value
   */
  function onActiveChange(value) {
    setConfigData((prev) => ({ ...prev, active: value }));
  }

  /**
   * This method will update the client id on input change
   * @param {*} e
   */
  function onClientIdChange(e) {
    setConfigData((prev) => ({ ...prev, client_id: e.target.value }));
  }

  /**
   * This method will update the client secret on input change
   * @param {*} e
   */
  function onClientSecretChange(e) {
    setConfigData((prev) => ({ ...prev, client_secret: e.target.value }));
  }

  /**
   * This method will update the username on input change
   * @param {*} e
   */
  function onUsernameChange(e) {
    setConfigData((prev) => ({ ...prev, username: e.target.value }));
  }

  /**
   * This method will update the password on input change
   * @param {*} e
   */
  function onPasswordChange(e) {
    setConfigData((prev) => ({ ...prev, password: e.target.value }));
  }

  /**
   * This method will update the api url on input change
   * @param {event} e
   */
  function onApiUrlChange(e) {
    setConfigData((prev) => ({ ...prev, api_url: e.target.value }));
  }

  /**
   * This method will update the auth url on input change
   * @param {event} e
   */
  function onAuthUrlChange(e) {
    setConfigData((prev) => ({ ...prev, auth_url: e.target.value }));
  }

  /**
   * This method will update the sync employees on toggle change
   * @param {bool} value
   */
  function onSyncEmployeesChange(value) {
    setConfigData((prev) => ({ ...prev, sync_employees: value }));
  }

  /**
   * This method will update the sync visitors on toggle change
   * @param {bool} value
   */
  function onSyncVisitorsChange(value) {
    setConfigData((prev) => ({ ...prev, sync_visitors: value }));
  }

  /**
   * This method will submit the form
   */
  async function onSubmit() {
    await callEvaApi(
      new EvaApiCall("integrations/salto/configs")
        .setParams(getConfigFetchParams())
        .setMethod("PUT")
        .setData(configData)
        .setLoadingGroup("salto_configs_page_save")
        .setAlertError(true)
        .setErrorState(setSubmitError)
        .setAlertSuccess(true)
        .setOnSuccess((response) => {
          authContext.fetchUser();
        }),
    );
  }

  return (
    <SaltoConfigsPageView
      configData={configData}
      saltoResources={saltoResources}
      visitorTypes={visitorTypes}
      setConfigData={setConfigData}
      onActiveChange={onActiveChange}
      onClientIdChange={onClientIdChange}
      onClientSecretChange={onClientSecretChange}
      onUsernameChange={onUsernameChange}
      onPasswordChange={onPasswordChange}
      onApiUrlChange={onApiUrlChange}
      onAuthUrlChange={onAuthUrlChange}
      onSyncEmployeesChange={onSyncEmployeesChange}
      onSyncVisitorsChange={onSyncVisitorsChange}
      onSubmit={onSubmit}
      submitError={submitError}
    />
  );
}

export default SaltoConfigsPage;
